import { createSelector } from 'reselect';
import { calendarAvailability, venueDetails } from 'store/saga/entities/bound';
import _sortBy from 'lodash/sortBy';
import _keyBy from 'lodash/keyBy';
import _filter from 'lodash/filter';
import { isAfter, isBefore, addDays, endOfDay, differenceInMinutes, addMinutes, parse, parseISO } from 'date-fns';

import { BLOCK_OFF_TIME_ITEM_TYPES } from 'constants/logic';
import { adjustDateForTimezone } from 'helpers/intl-fns';
import {
  selectUserId,
  selectActiveVenueId,
  selectVenueTimezone,
  selectRouteParams,
  selectActiveVenue,
} from 'selectors/general';
import { getKeyForUser } from 'helpers/local-storage';
import { getCalendarAvailabilityKey } from './helpers';
import {
  getAppointmentsLSKey,
  FILTER_ALL,
  FILTER_APPOINTMENTS,
  FILTER_CANCELLATIONS,
  FILTER_BLOCK_OFF_TIMES,
} from './reducer';

const CLASS_TIME_PARSE_FORMAT = 'yyyy-MM-dd HH:mm:ss';
const MINUTES_IN_DAY = 1440;

export const selectCachedAppointments = createSelector(selectUserId, selectActiveVenueId, (userId, venueId) =>
  getKeyForUser(userId, getAppointmentsLSKey({ venueId }), null)
);

export const selectAppointmentsData = createSelector(
  state => state,
  selectActiveVenue,
  (state, { venueId, timezone }) => {
    const key = getCalendarAvailabilityKey(venueId, timezone);

    return calendarAvailability.loaded(state, key) ? calendarAvailability.data(state, key) : undefined;
  }
);

const selectAppointmentsState = state => state.appointments || {};
const selectCurrentAppointments = createSelector(
  selectAppointmentsState,
  appointmentsState => appointmentsState.appointments || []
);
const selectAppointmentsUpdates = createSelector(
  selectAppointmentsState,
  appointmentsState => appointmentsState.updates || []
);

export const selectAppointmentsFilter = createSelector(
  selectAppointmentsState,
  appointmentsState => appointmentsState.filter || FILTER_ALL
);

export const selectAppointmentsLoading = createSelector(
  state => state,
  selectActiveVenue,
  (state, { venueId, timezone }) => {
    const key = getCalendarAvailabilityKey(venueId, timezone);
    return calendarAvailability.loading(state, key);
  }
);

export const selectAppointmentsLoaded = createSelector(
  selectAppointmentsState,
  appointmentsState => appointmentsState.loaded
);

export function getReservationsFilterForType(filter = '') {
  return (reservation = {}) => {
    if (filter === FILTER_APPOINTMENTS) {
      return !reservation.cancellationType;
    }

    if (filter === FILTER_CANCELLATIONS) {
      return !!reservation.cancellationType;
    }

    if (filter === FILTER_BLOCK_OFF_TIMES) {
      return false;
    }

    return true;
  };
}

export const selectAppointments = createSelector(
  selectCurrentAppointments,
  selectAppointmentsUpdates,
  selectAppointmentsFilter,
  selectVenueTimezone,
  (current, updates, filter, timezone) => {
    // Mark current reservations that have transitioned from not cancelled to cancelled as an update
    const updatesMap = _keyBy(updates, 'reservationId');
    const currentUpdated = current.map(reservation => {
      const updatedReservation = updatesMap[reservation.reservationId];

      // If an existing reservation transitioned from not cancelled to cancelled
      if (updatedReservation && !reservation.isCancelled && updatedReservation.isCancelled) {
        return { ...updatedReservation, isUpdate: true };
      }

      return updatedReservation || reservation;
    });

    // Mark new reservations as updates
    const currentMap = _keyBy(currentUpdated, 'reservationId');
    const reservationsWithUpdates = updates.reduce((reservations, reservation) => {
      if (!currentMap[reservation.reservationId]) {
        reservations.push({ ...reservation, isUpdate: true });
      }
      return reservations;
    }, currentUpdated);

    const reservations = _sortBy(
      reservationsWithUpdates.map(
        ({ date, createdOn, lastUpdated, startTime, endTime, endDateTime, startDateTime, ...reservation }) => {
          const startTimeParsed = parse(`${date} ${startTime}`, CLASS_TIME_PARSE_FORMAT, new Date());
          const endTimeParsed = parse(`${date} ${endTime}`, CLASS_TIME_PARSE_FORMAT, new Date());

          const duration = differenceInMinutes(endTimeParsed, startTimeParsed);

          return {
            ...reservation,
            venueTimezone: timezone,
            startDateTime: startTimeParsed,
            endDateTime: endTimeParsed,
            // These values are stored as strings in LocalStorage when we cache
            createdOn: typeof createdOn === 'string' ? parseISO(createdOn) : createdOn,
            lastUpdated: typeof lastUpdated === 'string' ? parseISO(lastUpdated) : lastUpdated,
            duration,
          };
        }
      ),
      'startDateTime'
    ).filter(getReservationsFilterForType(filter));

    return reservations;
  }
);

export const selectReservationFromRouteParam = createSelector(
  selectAppointments,
  selectRouteParams,
  (apps, { reservationId: rId }) => apps.find(({ reservationId }) => reservationId === rId) || {}
);

export const selectCurrentBlockOffTimes = createSelector(
  selectAppointmentsData,
  selectAppointmentsFilter,
  selectActiveVenue,
  (appts, filter, { isMsInventoryVenue }) => {
    if (!appts || ![FILTER_ALL, FILTER_BLOCK_OFF_TIMES].includes(filter) || !isMsInventoryVenue) return [];

    const msiTeacherTimeBlocks = !appts.msiTeacherTimeBlocks
      ? []
      : appts.msiTeacherTimeBlocks.map(({ startDatetime, endDatetime, teacherName, ...rest }) => ({
          itemType: BLOCK_OFF_TIME_ITEM_TYPES.PRACTITIONER,
          startDatetime: parseISO(startDatetime),
          endDatetime: parseISO(endDatetime),
          itemName: teacherName,
          ...rest,
        }));

    const venueTimeBlocks = !appts.venueTimeBlocks
      ? []
      : appts.venueTimeBlocks.map(({ startDatetime, endDatetime, ...rest }) => ({
          itemType: BLOCK_OFF_TIME_ITEM_TYPES.VENUE,
          startDatetime: parseISO(startDatetime),
          endDatetime: parseISO(endDatetime),
          ...rest,
        }));

    return [...msiTeacherTimeBlocks, ...venueTimeBlocks];
  }
);

export const selectCurrentBlockOffTimesBefore7days = createSelector(
  selectCurrentBlockOffTimes,
  selectVenueTimezone,
  (bot, timezone) =>
    _filter(bot, ({ startDatetime }) =>
      isBefore(startDatetime, endOfDay(adjustDateForTimezone(addDays(new Date(), 7), timezone)))
    )
);

export const selectCurrentBlockOffTimesAfter7days = createSelector(
  selectCurrentBlockOffTimes,
  selectVenueTimezone,
  (bot, timezone) =>
    _filter(bot, ({ startDatetime }) =>
      isAfter(startDatetime, endOfDay(adjustDateForTimezone(addDays(new Date(), 7), timezone)))
    )
);

export const selectVenueBookingWindowDays = createSelector(
  (state, venueId) => venueDetails.select(state, venueId),
  ({ data, error, loading: isLoading, loaded: isLoaded }) => {
    let bookingWindowDays = null;

    if (isLoaded) {
      const { reservableMinutesBefore } = data;

      if (reservableMinutesBefore < 720) {
        bookingWindowDays = 0;
      } else {
        bookingWindowDays = Math.ceil(reservableMinutesBefore / MINUTES_IN_DAY);
      }
    }

    return {
      error,
      isLoading,
      isLoaded,
      bookingWindowDays,
    };
  }
);

export const selectExternalClassIdByClassId = createSelector(
  (state, classId) => classId,
  selectAppointmentsData,
  (classId, appointmentsData) => appointmentsData?.externalClassIdsMap[classId]
);

export const selectIsServiceDetached = createSelector(
  (state, externalClassId) => ({ state, externalClassId }),
  ({ state, externalClassId }) => state.appointments.detachedServices.includes(externalClassId)
);

export const selectVenueBookingRestrictions = createSelector(
  (state, venueId) => venueDetails.select(state, venueId),
  ({ data, error, loading: isLoading, loaded: isLoaded }) => {
    if (!isLoaded) return {};

    const { reservableMinutesBefore, windowDaysBefore } = data;

    return {
      error,
      isLoading,
      isLoaded,
      restrictDaysBefore: Math.floor(reservableMinutesBefore / MINUTES_IN_DAY),
      restrictDaysAfter: windowDaysBefore,
      defaultDateOffsetInMinutes: reservableMinutesBefore % MINUTES_IN_DAY,
    };
  }
);

export const selectReservationDefaultStartDate = createSelector(
  (state, venueId) => venueDetails.select(state, venueId),
  selectVenueTimezone,
  selectReservationFromRouteParam,
  ({ data, loading: isLoading, loaded: isLoaded }, venueTimezone, reservation) => {
    const todayInVenueTimezone = adjustDateForTimezone(new Date(), venueTimezone);

    if (!isLoaded || isLoading || !reservation) return todayInVenueTimezone;

    const { reservableMinutesBefore, windowDaysBefore } = data;
    const { startDateTime } = reservation;

    const nearestPossibleResDate = addMinutes(todayInVenueTimezone, reservableMinutesBefore);
    const farthestPossibleResDate = addDays(todayInVenueTimezone, windowDaysBefore);

    if (isAfter(startDateTime, nearestPossibleResDate) && isBefore(startDateTime, farthestPossibleResDate)) {
      return startDateTime;
    }

    return nearestPossibleResDate;
  }
);
